import React, { useEffect } from 'react';
import {
  Box, CssBaseline, Typography, Stack, useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';

import ThemeToggleButton from './ThemeToggleButton';
import BracketLogo from './AppShell/BracketLogo';
import { GoogleAuth } from './shared/GoogleAuth';
import { MicrosoftAuth } from './shared/MicrosoftAuth';
import { tokens, categoryColors, radius } from '../themes/smartinventoryTheme';

import { useTranslation } from 'react-i18next';

const AVAILABLE_LANGS = ['en', 'fr', 'fr-CA', 'tr'];
const LANG_LABELS = { en: 'English', fr: 'Français', 'fr-CA': 'Français (CA)', tr: 'Türkçe' };

// Map browser locale -> one of our available languages
export function resolveAppLanguage(languages = navigator.languages, language = navigator.language) {
  const raw = (languages && languages[0]) || language || 'en';
  const locale = raw.replace('_', '-');

  if (AVAILABLE_LANGS.includes(locale)) return locale;

  const langOnly = locale.split('-')[0];
  if (AVAILABLE_LANGS.includes(langOnly)) return langOnly;

  if (langOnly === 'fr' && /-?CA$/i.test(locale)) return 'fr-CA';

  return 'en';
}

// The right-hand panel is a fixed dark-brand backdrop, independent of the
// app's light/dark toggle (which only affects the sign-in column) — so it
// reads from the dark token set directly rather than useDesignTokens().
const d = tokens.dark;

const SPARK_VALUES = [38, 52, 44, 62, 50, 70, 60, 78, 68, 84, 74, 92, 86, 100];
const STOCK_BARS = [
  { color: d.warning, pct: 82 },
  { color: d.success, pct: 64 },
  { color: d.info, pct: 48 },
  { color: categoryColors.dark.equipment.dot, pct: 31 },
];
const DIAMOND_PATTERN = [2, 0, 1, 0, 0, 1, 0, 2, 1, 0, 2, 0, 0, 2, 0, 1];
const DIAMOND_STYLES = [
  { bg: (t) => alpha(t.common.white, 0.03), border: (t) => alpha(d.accentInk, 0.14), dot: (t) => alpha(d.accentInk, 0.12), inner: 14 },
  { bg: () => alpha(d.accent, 0.2), border: () => alpha(d.accentInk, 0.34), dot: () => alpha(d.accentInk, 0.55), inner: 20 },
  { bg: (t) => alpha(t.common.white, 0.06), border: () => alpha(d.accentInk, 0.22), dot: () => alpha(d.accentInk, 0.28), inner: 10 },
];
const PROOF_STATS = [
  { value: '5,890', labelKey: 'current stock' },
  { value: '1,284', labelKey: 'active users' },
  { value: '2,410', labelKey: 'orders this month' },
];

const LoginPage = ({ setAuth, toggleTheme }) => {
  const isNarrow = useMediaQuery('(max-width:1024px)');
  const { t, i18n } = useTranslation();

  // Auto-select language based on browser locale; respect a previously chosen language if stored.
  useEffect(() => {
    const stored = localStorage.getItem('i18nextLng');
    if (stored && AVAILABLE_LANGS.includes(stored)) return;

    const preferred = resolveAppLanguage();
    i18n.changeLanguage(preferred);
    document.documentElement.lang = preferred;
  }, [i18n]);

  const cycleLanguage = () => {
    const idx = AVAILABLE_LANGS.indexOf(i18n.language);
    const next = AVAILABLE_LANGS[(idx + 1) % AVAILABLE_LANGS.length];
    i18n.changeLanguage(next);
    document.documentElement.lang = next;
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />

      <Box
        sx={{
          width: { xs: '100%', lg: 520 },
          flexShrink: 0,
          py: 5,
          px: { xs: 3, sm: 6 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 6, sm: 8 } }}>
          <BracketLogo />
          <ThemeToggleButton toggleTheme={toggleTheme} />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 372, mx: { xs: 'auto', lg: 0 }, width: '100%' }}>
          <Typography variant="h4" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {t('Welcome back')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 4 }}>
            {t('Sign in to your inventory workspace.', { defaultValue: 'Sign in to your inventory workspace.' })}
          </Typography>

          <Stack spacing={1.25}>
            <GoogleAuth setAuth={setAuth} />
            <MicrosoftAuth setAuth={setAuth} />
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 3, display: 'block' }}>
            {t('New to SmartInventory?', { defaultValue: 'New to SmartInventory?' })}{' '}
            <Typography component="span" variant="caption" fontWeight={600} sx={{ color: 'primary.main', cursor: 'pointer' }}>
              {t('Talk to sales', { defaultValue: 'Talk to sales' })}
            </Typography>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 3.5 }}>
          <Typography variant="caption" color="text.secondary">© 2026 SmartInventory</Typography>
          <Box onClick={cycleLanguage} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
            <LanguageIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">{LANG_LABELS[i18n.language] || LANG_LABELS.en}</Typography>
          </Box>
        </Box>
      </Box>

      {!isNarrow && (
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', bgcolor: d.canvas }}>
          <Box sx={{ position: 'absolute', inset: 0, background: `radial-gradient(120% 90% at 78% 8%, ${alpha(d.accentHover, 0.75)} 0%, ${alpha(d.surface, 0.9)} 42%, ${d.canvas} 78%)` }} />
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: (theme) => `linear-gradient(${alpha(theme.palette.common.white, 0.045)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(theme.palette.common.white, 0.045)} 1px, transparent 1px)`,
            backgroundSize: '44px 44px',
          }} />
          <Box sx={{ position: 'absolute', top: -180, right: -200, width: 720, height: 720, borderRadius: '50%', border: '1px solid', borderColor: alpha(d.accentInk, 0.22) }} />
          <Box sx={{ position: 'absolute', top: -100, right: -120, width: 560, height: 560, borderRadius: '50%', border: '1px solid', borderColor: alpha(d.accentInk, 0.16) }} />
          <Box sx={{ position: 'absolute', top: -20, right: -40, width: 400, height: 400, borderRadius: '50%', border: '1px solid', borderColor: alpha(d.accentInk, 0.1) }} />
          <Box sx={{ position: 'absolute', bottom: -260, left: -160, width: 620, height: 620, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(d.accent, 0.34)} 0%, ${alpha(d.accent, 0)} 68%)` }} />

          <Box sx={{
            position: 'absolute', top: 96, left: 74,
            display: 'grid', gridTemplateColumns: 'repeat(4, 92px)', gridTemplateRows: 'repeat(4, 92px)', gap: '10px',
            transform: 'rotate(45deg) skewX(-8deg)', opacity: 0.9,
          }}
          >
            {DIAMOND_PATTERN.map((styleIdx, i) => {
              const style = DIAMOND_STYLES[styleIdx];
              return (
                <Box key={i} sx={(theme) => ({
                  borderRadius: `${radius.lg}px`, border: '1px solid', borderColor: style.border(theme.palette),
                  bgcolor: style.bg(theme.palette), display: 'flex', alignItems: 'center', justifyContent: 'center',
                })}
                >
                  <Box sx={(theme) => ({ width: style.inner, height: style.inner, borderRadius: `${radius.sm}px`, bgcolor: style.dot(theme.palette) })} />
                </Box>
              );
            })}
          </Box>

          <Box sx={{
            position: 'absolute', top: 148, right: 88, width: 268,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.07), border: '1px solid', borderColor: (theme) => alpha(theme.palette.common.white, 0.14),
            borderRadius: `${radius.xl}px`, p: '18px 20px', boxShadow: (theme) => theme.shadows[6],
          }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: d.accentInk }}>{t('Revenue · this month', { defaultValue: 'Revenue · this month' })}</Typography>
              <Typography variant="caption" component="span" fontWeight={600} sx={{ px: 1, py: 0.25, borderRadius: `${radius.pill}px`, bgcolor: alpha(d.success, 0.18), color: d.successInk }}>▲ 12%</Typography>
            </Box>
            <Typography variant="h5" fontWeight={600} sx={{ color: d.ink }}>$18,940</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '5px', height: 44, mt: 1.75 }}>
              {SPARK_VALUES.map((v, i) => (
                <Box key={i} sx={{ flex: 1, height: `${Math.max(5, Math.round(v * 0.44))}px`, borderRadius: '3px', bgcolor: i >= 12 ? d.accentInk : alpha(d.accentInk, 0.34) }} />
              ))}
            </Box>
          </Box>

          <Box sx={{
            position: 'absolute', top: 372, right: 148, width: 234,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.07), border: '1px solid', borderColor: (theme) => alpha(theme.palette.common.white, 0.14),
            borderRadius: `${radius.xl}px`, p: '16px 18px', boxShadow: (theme) => theme.shadows[6],
          }}
          >
            <Typography variant="caption" sx={{ color: d.accentInk, display: 'block', mb: 1.25 }}>{t('Stock by category', { defaultValue: 'Stock by category' })}</Typography>
            {STOCK_BARS.map((bar, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 1 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: bar.color, flexShrink: 0 }} />
                <Box sx={{ flex: 1, height: 6, borderRadius: '3px', bgcolor: (theme) => alpha(theme.palette.common.white, 0.1) }}>
                  <Box sx={{ width: `${bar.pct}%`, height: '100%', borderRadius: '3px', bgcolor: bar.color }} />
                </Box>
                <Typography variant="caption" sx={{ color: d.accentInk, width: 30, textAlign: 'right' }}>{bar.pct}%</Typography>
              </Box>
            ))}
          </Box>

          {/* Anchored a fixed gap below the stock card (top:372, ~138px tall)
              rather than from the panel bottom — the stock card's height
              never changes, so this can never collide with it, at any
              viewport height. The bottom content block sits further left
              (x < 1054) than this column (x > 1057), so the two never
              overlap horizontally regardless of vertical position. */}
          <Box sx={{
            position: 'absolute', top: 536, right: 96, display: 'flex', alignItems: 'center', gap: 1.4,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.07), border: '1px solid', borderColor: (theme) => alpha(theme.palette.common.white, 0.14),
            borderRadius: `${radius.pill}px`, py: 1.1, pl: 1.1, pr: 2.25, boxShadow: (theme) => theme.shadows[6],
          }}
          >
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: alpha(d.success, 0.18), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckIcon fontSize="small" sx={{ color: d.successInk }} />
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ color: d.ink }}>{t('Order #1042 accepted', { defaultValue: 'Order #1042 accepted' })}</Typography>
          </Box>

          <Box sx={{ position: 'absolute', bottom: 74, left: 74, right: 74 }}>
            <BracketLogo sx={{ mb: 2.5 }} bracketColor={d.accentInk} wordmarkColor={d.ink} accentColor={d.accentInk} />
            <Typography variant="h4" fontWeight={600} sx={{ color: d.ink, lineHeight: 1.25, maxWidth: 460 }}>
              {t('Everything, in one place.', { defaultValue: 'Everything, in one place.' })}
            </Typography>
            <Stack direction="row" spacing={3.5} sx={{ mt: 2.75 }}>
              {PROOF_STATS.map((stat) => (
                <Box key={stat.labelKey}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: d.ink }}>{stat.value}</Typography>
                  <Typography variant="caption" sx={{ color: d.muted }}>{t(stat.labelKey)}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LoginPage;
